import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { Apollo } from 'src/gql/generated';
import { nonSigningPhaseTypename, workflowTypename } from 'src/helpers';
import { useDispatchNotification } from 'src/helpers/snackbars';

export function useGetTask(taskId: UUID): {
  loading: boolean;
  refetch: Apollo.GetTaskQueryHookResult['refetch'];
  task: NonNullable<Apollo.GetTaskQueryHookResult['data']>['task'] | null;
} {
  const { data, loading, refetch } = Apollo.useGetTaskQuery({ variables: { taskId } });
  return { loading, refetch, task: data?.task ?? null };
}

export function useGetTaskWithComments(taskId: UUID): {
  loading: boolean;
  task: NonNullable<Apollo.GetTaskWithCommentsQueryResult['data']>['task'] | null;
} {
  const { data, loading } = Apollo.useGetTaskWithCommentsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { taskId },
  });

  return { loading: loading && !data, task: data?.task ?? null };
}

export function useCreateTask(): {
  createTask: (
    workflowId: UUID,
    options: Parameters<Apollo.CreateTaskMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateTaskMutation>>;
  loading: boolean;
} {
  const [createTask, { loading }] = Apollo.useCreateTaskMutation();
  const safeCreateTask = useCallback(
    (workflowId: UUID, options: Parameters<Apollo.CreateTaskMutationHookResult[0]>[0]) => {
      const phaseId = options.variables?.input.props.phaseId;
      return createTask({
        ...options,
        update(cache, { data }) {
          const newTaskRef = cache.writeFragment({
            data: data?.createTask.task,
            fragment: gql`
              fragment NewTask on Task {
                __typename
                id
              }
            `,
          });
          if (!newTaskRef) return;

          const appendRef = (
            existingRefs: readonly Reference[] = [],
            { readField }: { readField: <T>(field: string, ref: Reference) => T | undefined }
          ) =>
            uniqWith(
              (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
              [...existingRefs, newTaskRef]
            );

          cache.modify({
            fields: { tasks: appendRef },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });

          if (phaseId) {
            cache.modify({
              fields: { tasks: appendRef },
              id: cache.identify({ __typename: nonSigningPhaseTypename, id: phaseId }),
            });
          }
        },
      });
    },
    [createTask]
  );

  return {
    createTask: safeCreateTask,
    loading,
  };
}

type SetTaskAssigneesTask = Apollo.SetTaskAssigneesMutation['setTaskAssignees']['task'];
type OptimisticTaskAssignment = SetTaskAssigneesTask & {
  nextPermissions: SetTaskAssigneesTask['assignments'][number]['permission'][];
};

interface SetTaskAssigneesInput {
  input: Apollo.SetTaskAssigneesInput;
  workflowId: UUID;
  optimistic?: OptimisticTaskAssignment;
}

export function useSetTaskAssignees(): {
  loading: boolean;
  /**
   * Returns the mutate function's full result (not just ApolloLink.Result) so callers can
   * check `error` — errorPolicy 'all' resolves with both data and error on a failed mutation.
   */
  setTaskAssignees: (
    options: SetTaskAssigneesInput
  ) => Promise<Awaited<ReturnType<Apollo.SetTaskAssigneesMutationHookResult[0]>>>;
} {
  const [setTaskAssignees, { loading }] = Apollo.useSetTaskAssigneesMutation();
  const safeSetTaskAssignees = useCallback(
    async ({ input, optimistic, workflowId }: SetTaskAssigneesInput) => {
      return setTaskAssignees({
        optimisticResponse: optimistic
          ? {
              __typename: 'Mutation',
              setTaskAssignees: {
                __typename: 'TaskResponse',
                task: {
                  __typename: 'Task',
                  alineAiRunMessageId: optimistic.alineAiRunMessageId,
                  /**
                   * Only removals are certain before the server answers; whether a new assignee is
                   * external or required is its call, so additions land with the response.
                   */
                  assignments: optimistic.assignments.filter(assignment =>
                    optimistic.nextPermissions.some(
                      permission => permission.id === assignment.permission.id
                    )
                  ),
                  counterpartyName: optimistic.counterpartyName,
                  hasExternalAssignees: optimistic.hasExternalAssignees,
                  id: optimistic.id,
                  isDelegatedToAi: optimistic.isDelegatedToAi,
                  startedAt: optimistic.startedAt,
                  status: optimistic.status,
                  viewerActionRecipients: optimistic.viewerActionRecipients,
                },
              },
            }
          : undefined,
        /**
         * Reassignment changes the (locked) assignees, so the derived activity-subscriber avatar/lock
         * state is now stale (the mutation response doesn't touch those fields). Refetch with the real
         * ids — a name-only refetch would also match the subscriber control's skipped placeholder
         * query (empty-string id) and fire it with `""`.
         */
        refetchQueries: [
          { query: Apollo.GetTaskSubscribersDocument, variables: { taskId: input.taskId } },
          { query: Apollo.GetWorkflowSubscribersDocument, variables: { workflowId } },
        ],
        update(cache, { data }) {
          cache.modify({
            fields: {
              permissions: (existingRefs: readonly Reference[] = [], { readField }) => {
                const newPermissionRefs = (data?.setTaskAssignees.task.assignments ?? []).flatMap(
                  ({ permission }) => {
                    const ref = cache.writeFragment({
                      data: permission,
                      fragment: gql`
                        fragment NewTaskPermission on WorkflowPermission {
                          __typename
                          id
                        }
                      `,
                    });
                    return ref ? [ref] : [];
                  }
                );

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...existingRefs, ...newPermissionRefs]
                );
              },
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });
        },
        variables: { input },
      });
    },
    [setTaskAssignees]
  );

  return {
    loading,
    setTaskAssignees: safeSetTaskAssignees,
  };
}

export function useDeleteTask() {
  const [deleteTask, { loading }] = Apollo.useDeleteTaskMutation();
  const safeDeleteTask = useCallback(
    async (taskId: UUID, workflowId: UUID, phaseId?: UUID) => {
      return deleteTask({
        update: cache => {
          const tasksKey: keyof Apollo.Workflow = 'tasks';
          const filterTask = (
            existingTaskRefs: readonly Reference[] = [],
            { readField }: { readField: <T>(field: string, ref: Reference) => T | undefined }
          ) => existingTaskRefs.filter(ref => readField<UUID>('id', ref) !== taskId);

          cache.modify({
            fields: { [tasksKey]: filterTask },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });

          if (phaseId) {
            cache.modify({
              fields: { tasks: filterTask },
              id: cache.identify({ __typename: nonSigningPhaseTypename, id: phaseId }),
            });
          }
        },
        variables: { taskId },
      });
    },
    [deleteTask]
  );

  return {
    deleteTask: safeDeleteTask,
    loading,
  };
}

export function useGetMyTasks(options?: Parameters<typeof Apollo.useGetUserTasksQuery>[0]): {
  loading: boolean;
  tasks: NonNullable<Apollo.GetUserTasksQueryResult['data']>['userIncompleteTasks'];
} {
  const { data, loading } = Apollo.useGetUserTasksQuery(options);
  return { loading, tasks: data?.userIncompleteTasks ?? [] };
}

export function useGetTaskStatus(taskId: UUID): {
  loading: boolean;
  task: NonNullable<Apollo.GetTaskStatusQueryHookResult['data']>['task'];
} {
  const { data, loading } = Apollo.useGetTaskStatusQuery({ variables: { taskId } });

  return {
    loading,
    task: data?.task ?? null,
  };
}

/**
 * Allows user to mark a given task complete/incomplete.
 */
export function useChangeTaskStatus(taskId: UUID) {
  const { data } = Apollo.useGetTaskQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { taskId },
  });
  const [changeTaskStatus, { loading }] = Apollo.useChangeTaskStatusMutation();
  const { changeStatus: cannotChangeStatusReason = null } =
    data?.task?.requesterDisallowedReason ?? {};
  const dispatchWarning = useDispatchNotification({
    message: cannotChangeStatusReason ?? '',
    variant: 'warning',
  });
  const markComplete = useCallback(
    async (options: { comment?: string | null; confirmUnpark?: boolean } = {}) => {
      if (cannotChangeStatusReason) {
        dispatchWarning();
        return;
      }

      return changeTaskStatus({
        variables: {
          input: {
            comment: options.comment?.trim() || null,
            confirmUnpark: options.confirmUnpark ?? false,
            isCompleted: true,
            taskId,
          },
        },
      });
    },
    [changeTaskStatus, taskId, cannotChangeStatusReason, dispatchWarning]
  );
  const markNotComplete = useCallback(
    async (options: { startNewRound?: boolean } = {}) => {
      if (cannotChangeStatusReason) {
        dispatchWarning();
        return;
      }

      return changeTaskStatus({
        variables: {
          input: { isCompleted: false, startNewRound: options.startNewRound ?? false, taskId },
        },
      });
    },
    [changeTaskStatus, taskId, cannotChangeStatusReason, dispatchWarning]
  );

  return {
    cannotChangeStatusReason,
    loading,
    markComplete,
    markNotComplete,
    task: data?.task ?? null,
  };
}
